
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, Filter } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/components/ui/sonner";
import { Database } from "@/integrations/supabase/types";
import { createSearchParams, useSearchParams } from "react-router-dom";

type Entry = Database["public"]["Tables"]["entries"]["Row"];

export const SearchBar = () => {
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize query from URL params when component mounts
  useEffect(() => {
    const queryParam = searchParams.get("query");
    if (queryParam) {
      setQuery(queryParam);
    }
  }, []);

  // Update URL with search query
  const updateSearchParams = (newQuery: string) => {
    if (location.pathname === "/dashboard") {
      const params = new URLSearchParams(searchParams);
      if (newQuery) {
        params.set("query", newQuery);
      } else {
        params.delete("query");
      }
      setSearchParams(params);
    }
  };

  // Update search query when user types
  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    updateSearchParams(newQuery);
  };

  const clearSearch = () => {
    setQuery("");
    updateSearchParams("");
  };

  const navigateToEntry = (id: string) => {
    navigate(`/dashboard/entry/${id}`);
    setOpen(false);
  };

  return (
    <div className="relative w-full">
      <div className="flex gap-1">
        <div className="flex-1 relative">
          <Input
            placeholder="Search entries..."
            value={query}
            onChange={handleQueryChange}
            className="pl-8"
          />
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        </div>
        {query ? (
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 shrink-0"
            onClick={clearSearch}
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
};
